import React, { useEffect, useMemo } from "react";
import { Layer, Popup, Source, useMap } from "react-map-gl/maplibre";
import { ISTATUS } from "../../../../models/common/CommonModels";
import { filledColorSelection } from "../../../../utils/common/CommonHelperFunctions";
import { IRSCInfo } from "../../../../utils/maps/MapConstants";
import { splitIntoThreeWithGaps } from "../../../../utils/maps/MapHelperFunctions";
import RSCMarkerTooltip from "../../../osm-maps/map-markers/runway-markers/RSCMarkerTooltip";
import {
  RUNWAY_TOOLTIP_PRIORITY,
  useMapTooltip,
} from "../../context/MapTooltipContext";
import { toLngLat } from "../../utils/coordinates";

interface RunwayMarkerProps {
  airportCode: string;
  runwayId: string;
  runwayName: string;
  start: [number, number];
  end: [number, number];
  rscInfo: IRSCInfo;
  status: [ISTATUS, ISTATUS, ISTATUS];
  gapMeters?: number;
}

const RunwayMarker: React.FC<RunwayMarkerProps> = ({
  airportCode,
  runwayId,
  runwayName,
  start,
  end,
  rscInfo,
  status,
  gapMeters = 6,
}) => {
  const { current: map } = useMap();

  const safeId = runwayId.replace(/[^a-zA-Z0-9-_]/g, "-");
  const hitLayerId = `runway-hit-${safeId}`;

  const { isTooltipVisible, showTooltip, hideTooltip } = useMapTooltip(
    `runway:${airportCode}:${runwayId}`,
    RUNWAY_TOOLTIP_PRIORITY
  );

  const segments = useMemo(
    () => splitIntoThreeWithGaps(start, end, gapMeters),
    [start, end, gapMeters]
  );

  const data = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: segments.map((segment, index) => ({
        type: "Feature" as const,
        properties: {
          color: filledColorSelection(status[index]),
          segmentIndex: index,
        },
        geometry: {
          type: "LineString" as const,
          coordinates: segment.map(toLngLat),
        },
      })),
    }),
    [segments, status]
  );

  const midpoint: [number, number] = [
    (start[0] + end[0]) / 2,
    (start[1] + end[1]) / 2,
  ];
  const [longitude, latitude] = toLngLat(midpoint);

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const handleMouseEnter = () => {
      map.getCanvas().style.cursor = "pointer";
      showTooltip();
    };
    const handleMouseLeave = () => {
      map.getCanvas().style.cursor = "";
      hideTooltip();
    };

    map.on("mouseenter", hitLayerId, handleMouseEnter);
    map.on("mouseleave", hitLayerId, handleMouseLeave);

    return () => {
      map.off("mouseenter", hitLayerId, handleMouseEnter);
      map.off("mouseleave", hitLayerId, handleMouseLeave);
      map.getCanvas().style.cursor = "";
    };
  }, [hideTooltip, hitLayerId, map, showTooltip]);

  return (
    <>
      <Source id={`runway-source-${safeId}`} type="geojson" data={data}>
        {/* Interleave each halo/status pair so adjacent colors cannot cover the white end border. */}
        {segments.flatMap((_segment, index) => [
          <Layer
            key={`runway-halo-${safeId}-${index}`}
            id={`runway-halo-${safeId}-${index}`}
            type="line"
            filter={["==", ["get", "segmentIndex"], index]}
            layout={{ "line-cap": "round", "line-join": "round" }}
            paint={{
              "line-color": "#fff",
              "line-width": 16,
              "line-opacity": 1,
            }}
          />,
          <Layer
            key={`runway-status-${safeId}-${index}`}
            id={`runway-status-${safeId}-${index}`}
            type="line"
            filter={["==", ["get", "segmentIndex"], index]}
            layout={{ "line-cap": "round", "line-join": "round" }}
            paint={{
              "line-color": ["get", "color"],
              "line-width": 10,
              "line-opacity": 1,
            }}
          />,
        ])}
        <Layer
          id={hitLayerId}
          type="line"
          layout={{ "line-cap": "round", "line-join": "round" }}
          paint={{
            "line-color": "rgba(0, 0, 0, 0)",
            "line-width": 24,
          }}
        />
      </Source>
      {isTooltipVisible && (
        <Popup
          longitude={longitude}
          latitude={latitude}
          anchor="bottom"
          offset={18}
          closeButton={false}
          closeOnClick={false}
          maxWidth="none"
          className="runway-tooltip-popup"
        >
          <RSCMarkerTooltip
            airportCode={airportCode}
            runwayName={runwayName}
            current={rscInfo.current}
            prior={rscInfo.prior}
          />
        </Popup>
      )}
    </>
  );
};

export default RunwayMarker;
